import { Carrier } from './world/Carrier.js'
import { RailsFlight } from './rails/RailsFlight.js'
import { PilotControls } from './procedures/PilotControls.js'
import { ProcedureTrainer, describeKey } from './procedures/ProcedureTrainer.js'
import { DcsKeymap, BINDS } from './input/DcsKeymap.js'
import { SceneRoot } from './scene/SceneRoot.js'
import { VIEWS } from './scene/CameraRig.js'
import { MouseLook } from './input/MouseLook.js'
import { TouchControls } from './input/TouchControls.js'
import { detect } from './util/device.js'
import { HUD } from './hud/HUD.js'
import { Guidance } from './ui/Guidance.js'
import { bus } from './util/events.js'
import { AIRFRAME_MODEL, SIM } from './config/constants.js'
import { RAD } from './util/units.js'

/**
 * F-14B CASE 1 procedures trainer.
 *
 * The aeroplane flies the pattern. You run the checklist.
 *
 * That is the whole design, and it is a deliberate inversion of what a flight
 * simulator normally does. Learning to fly a Tomcat around the boat and
 * learning the CASE 1 flow are two different skills, and trying to acquire
 * them simultaneously means failing at both: you spend the downwind fighting
 * the aeroplane and never once think about the gear. So here the flying is
 * taken away entirely — the path is commanded, the ball is always centred —
 * and every ounce of attention goes on what to do and when.
 *
 * FRAME ORDER, AND WHY IT IS THIS ORDER
 *
 *   1. the trainer's time scale, from the previous frame's hold state
 *   2. the world advances by the SCALED dt: ship and aeroplane together, so
 *      their relative geometry never diverges
 *   3. your controls advance by the REAL dt — the throttle still moves and the
 *      gear still travels while the world is stopped, because otherwise you
 *      could not perform the step you are being held for
 *   4. the trainer looks at what you have done and decides whether to hold
 *   5. render, then HUD, then the DOM
 *
 * Step 3 is the one that is easy to get wrong and impossible to spot in a
 * screenshot: freeze the controls along with the world and the hold becomes a
 * deadlock you can only escape by reloading.
 */

const guidance = new Guidance()
const container = document.getElementById('app')

const carrier = new Carrier()
const controls = new PilotControls()
const trainer = new ProcedureTrainer(bus)
const rails = new RailsFlight(carrier, (legId) => trainer.canLeaveLeg(legId), {
  // The aerodynamics reads the flap position off the PILOT'S handle, not off
  // the leg schedule. Otherwise the indexer shows a lit on-speed doughnut the
  // moment you roll out downwind, flaps up, and the trainer contradicts the
  // reason it just gave you for lowering them.
  flapSource: () => controls.flapNorm,
})

let scene = null
let hud = null
let keymap = null
let mouse = null
let touch = null
let device = null

let running = false
let paused = false
let lastTime = 0
let accumulator = 0
let debriefShown = false

/* ===================================================================== *
 *  RESET                                                                *
 * ===================================================================== */

function restart(fromLeg = 'INITIAL') {
  carrier.reset?.()
  rails.reset(fromLeg)
  controls.reset({ dirty: fromLeg === 'GROOVE' })
  trainer.reset(fromLeg)

  // Re-arm anything still physically held. R and C are exactly what you reach
  // for while stuck holding Page Down, and a reset that forgot the key was
  // down left the throttle dead until you released and pressed it again.
  for (const id of keymap?.heldBinds ?? []) controls.hold(id, true)
  guidance.hideDebrief()
  debriefShown = false
  accumulator = 0
  guidance.toast(fromLeg === 'GROOVE' ? 'IN THE GROOVE' : 'AT THE INITIAL')
}

/* ===================================================================== *
 *  INPUT                                                                *
 * ===================================================================== */

/**
 * Trainer commands are handled here; everything else is an aircraft command
 * and goes to PilotControls. The split matters: the aircraft commands are DCS
 * bindings that are supposed to transfer, and they must behave the same way
 * whatever the trainer thinks of them.
 */
function onPress(id) {
  switch (id) {
    case 'view1': return scene.rig.setView(VIEWS.COCKPIT)
    case 'view2': return scene.rig.setView(VIEWS.CHASE)
    case 'view3': return scene.rig.setView(VIEWS.ORBIT)
    case 'view4': return scene.rig.setView(VIEWS.LSO)
    case 'view5': return scene.rig.setView(VIEWS.PLAT)
    case 'view6': return scene.rig.setView(VIEWS.TOWER)
    case 'recentre': return scene.rig.recentreLook()
    // Three centimetres a press. The whole usable range is about twenty, and
    // the difference between seeing over the nose and seeing the top of the
    // instrument panel is under ten — so a coarse step would step straight
    // over the answer.
    case 'seatUp': return void guidance.toast(`SEAT ${scene.rig.adjustSeat(0.03).toFixed(2)} m`)
    case 'seatDown': return void guidance.toast(`SEAT ${scene.rig.adjustSeat(-0.03).toFixed(2)} m`)
    case 'restart': return restart('INITIAL')
    case 'startGroove': return restart('GROOVE')
    case 'help': return guidance.toggleHelp()
    case 'pause':
      paused = !paused
      guidance.toast(paused ? 'PAUSED' : 'RUNNING')
      return
    default: {
      const message = controls.press(id, { simTime: rails.time })
      if (message) guidance.toast(message)
      if (BINDS[id]?.held) controls.hold(id, true)
    }
  }
}

function onRelease(id) {
  if (BINDS[id]?.held) controls.hold(id, false)
}

/**
 * A key that is not bound, or is bound but was pressed with the wrong
 * modifiers.
 *
 * This is where the most useful message in the program gets produced. Every
 * other simulator puts the tailhook on H. DCS puts it on LCtrl+H. Someone
 * pressing H and getting silence would reasonably conclude the trainer is
 * broken, so instead they get told exactly what the command is.
 */
function onUnbound(code, heldMods) {
  const message = trainer.noteUnbound(code, heldMods)
  if (message) guidance.toast(message, 2.6, true)
  else if (!heldMods.length && code.startsWith('Key')) {
    guidance.toast(`${describeKey(code)} is not bound to anything`, 1.4, true)
  }
}

/* ===================================================================== *
 *  FRAME                                                                *
 * ===================================================================== */

function frame(now) {
  requestAnimationFrame(frame)
  if (!running) return

  const t = now / 1000
  const dt = Math.min(lastTime ? t - lastTime : 1 / 60, 0.25)
  lastTime = t

  if (!paused) {
    // The world advances on scaled time; you advance on real time.
    const scaled = dt * trainer.timeScale

    // Fixed steps so the pattern is identical on a 60 Hz laptop and a 144 Hz
    // monitor. The guidance is graded on where the aeroplane is, so "where the
    // aeroplane is" must not depend on the display.
    accumulator += scaled
    let steps = 0
    while (accumulator >= SIM.DT && steps < SIM.MAX_SUBSTEPS) {
      carrier.update(SIM.DT)
      rails.step(SIM.DT)
      accumulator -= SIM.DT
      steps++
    }
    if (steps === 0) {
      // Fully held. The rails still get a look so the hold can lift the
      // instant the step is done, rather than on the next unfrozen frame.
      rails.step(0)
    }
    if (accumulator > SIM.DT * SIM.MAX_SUBSTEPS) accumulator = 0

    controls.update(dt)
    controls.writeInto(rails.state)
    trainer.update(dt, rails, controls)
  }

  const g = trainer.snapshot(rails, controls)
  const approach = rails.approach()
  // `snapshot` is a getter that builds a fresh object; read it once a frame
  // rather than once per consumer.
  const systems = controls.snapshot

  scene.update({
    state: rails.state,
    carrier,
    dt,
    systems,
    // The stick in the cockpit is not yours, but it should still move — a
    // control column sitting dead centre through a 60 degree break is the
    // single most obvious tell that the aeroplane is on rails. Derived from the
    // attitude it is actually holding, so it leads and trails the way a stick
    // does.
    inputs: {
      roll: Math.max(-1, Math.min(1, rails.bankDeg / 55)),
      pitch: Math.max(-1, Math.min(1, (rails.state.alphaDeg - 6) / 8)),
      yaw: 0,
      throttle: controls.throttle,
    },
    bearingToShipDeg:
      Math.atan2(
        carrier.position.x - rails.state.position.x,
        -(carrier.position.z - rails.state.position.z),
      ) * RAD,
    ballNorm: approach.ballNorm,
    ballInRange: rails.leg.id === 'GROOVE' || rails.leg.id === 'APPROACH_TURN',
    waveoff: false,
    cut: false,
  })

  // Render before the HUD: the HUD projects the pitch ladder through the
  // camera's world matrix, and that matrix is only current after a render.
  scene.render()

  hud.draw({
    // The DOM panels already carry the readouts and the cue, and on a phone
    // there is not room for both. See HUD.draw.
    compact: !!device?.touch,

    state: rails.state,
    camera: scene.camera,
    cockpit: scene.rig.isCockpit,
    approach: rails.leg.id === 'GROOVE' || rails.leg.id === 'APPROACH_TURN' ? approach : null,
    pattern: {
      phaseLabel: g.holding ? `WAITING — ${g.holdTitle}` : g.legLabel,
      cue: g.current && !g.current.informational ? `${g.current.key ?? ''}  ${g.current.title}` : '',
      cueUrgent: g.holding,
    },
    systems,
  })

  guidance.update(g, rails.state)
  // The trainer always knows what it wants next, which is what makes a single
  // large button a workable control surface on a phone.
  touch?.update(g.current, g.holding)

  if (g.complete && !debriefShown) {
    debriefShown = true
    guidance.showDebrief(trainer.debrief(rails))
  }
}

/* ===================================================================== *
 *  BOOT                                                                 *
 * ===================================================================== */

async function boot() {
  try {
    // What is this running on? The answer changes two things: how hard the
    // scene may push the GPU, and whether there is a keyboard to press any of
    // this with. See util/device.js.
    device = detect(window)
    console.info(
      `[device] ${device.tier} tier, ${device.touch ? 'touch' : 'keyboard'}` +
        `${device.touchForced ? ' (forced by ?touch=)' : ''}, ` +
        `dpr ${device.env.pixelRatio}, ${device.env.cores} cores, ` +
        `short side ${device.env.shortSideCss} css px`,
    )

    guidance.progress(0.15, 'building the world')
    scene = new SceneRoot(container, device.settings)

    guidance.progress(0.6, 'wiring the controls')
    hud = new HUD(container)

    keymap = new DcsKeymap({ onPress, onRelease, onUnbound }).attach(window)
    mouse = new MouseLook(scene.canvas, scene.rig)

    // On-screen controls. The keyboard stays live either way — a tablet with a
    // keyboard should be able to use both, and nothing here disables anything.
    touch = new TouchControls(container, onPress, onRelease)
    touch.setEnabled(device.touch)
    // And switch them on anyway if a finger ever appears. See watchForTouch:
    // the boot-time guess is a prediction, this is evidence.
    //
    // Unless `?touch=0` said otherwise. That flag exists to force the keyboard
    // path on a machine that has both, and a flag that cannot survive being
    // touched is no use for testing the thing it was added to test.
    if (!device.touchLocked) touch.watchForTouch(window)

    bus.on('stepDone', ({ late }) => {
      if (late) guidance.toast('CAUGHT UP', 0.9, true)
    })

    guidance.progress(0.85, 'setting up the pattern')
    restart('INITIAL')

    // Attribution for the aircraft model. Required by its licence.
    scene.modelsReady
      ?.then(({ airframeLoaded }) => {
        if (airframeLoaded && AIRFRAME_MODEL.credit) guidance.setCredit(AIRFRAME_MODEL.credit)
      })
      .catch(() => {})

    guidance.progress(1, 'ready')
    guidance.bootComplete()

    running = true
    lastTime = 0
    requestAnimationFrame(frame)

    // Handy for poking at the trainer from the console.
    Object.assign(window, { __sim: { rails, carrier, controls, trainer, scene, restart, device, touch } })
  } catch (err) {
    console.error(err)
    guidance.bootError(err)
  }
}

boot()

void keymap
void mouse
